import { Button, DatePicker } from 'antd';
import moment, { Moment } from 'moment';
import AppTable from '../AppTable';
import LazyLoader from '../LazyLoader';
import FriendlyDateTime from '../../types/FriendlyDateTime';
import { AuditLogLine } from '../../types/AuditLog';
import styles from './DataStoreAuditLog.module.less';

const { RangePicker } = DatePicker;

interface Props {
    lines: AuditLogLine[];
    loading: boolean;
    loadingMore: boolean;
    hasMore: boolean;
    onLoadMore: () => void;
    from?: string;
    to?: string;
    onFilterChange?: (from?: string, to?: string) => void;
    onClearFilters?: () => void;
    testId?: string;
}

const hasData = (record: AuditLogLine) =>
    record.data !== null &&
    record.data !== undefined &&
    Object.keys(record.data).length > 0;

const DataStoreAuditLog = ({
    lines,
    loading,
    loadingMore,
    hasMore,
    onLoadMore,
    from,
    to,
    onFilterChange = () => {},
    onClearFilters = () => {},
    testId = 'DataStoreAuditLog',
}: Props) => {
    const hasActiveFilter = !!(from || to);

    const formatDate = (time: string) =>
        new FriendlyDateTime({ date: time }).getDate();

    const handleRangeChange = (
        dates: [Moment | null, Moment | null] | null
    ) => {
        if (!dates || (!dates[0] && !dates[1])) {
            onClearFilters();
        } else {
            const newFrom = dates[0]
                ? dates[0].clone().startOf('day').toISOString()
                : undefined;
            const newTo = dates[1]
                ? dates[1].clone().endOf('day').toISOString()
                : undefined;
            onFilterChange(newFrom, newTo);
        }
    };

    const rangeValue: [Moment, Moment] | null =
        from || to
            ? [from ? moment(from) : moment(0), to ? moment(to) : moment()]
            : null;

    const columns = [
        {
            title: 'When',
            key: 'time',
            dataIndex: 'time',
            render: (time: string) => formatDate(time),
        },
        {
            title: 'User',
            key: 'user',
            dataIndex: 'user',
        },
        {
            title: 'Type',
            key: 'type',
            dataIndex: 'type',
        },
        {
            title: 'Description',
            key: 'text',
            dataIndex: 'text',
        },
    ];

    const expandedRowRender = (record: AuditLogLine) => (
        <pre className={styles.DataJson}>
            <code>{JSON.stringify(record.data, null, 2)}</code>
        </pre>
    );

    return (
        <section data-testid={testId} className={styles.DataStoreAuditLog}>
            <h4 className={styles.AuditLogLabel}>Audit Log</h4>
            <div className={styles.FilterBar}>
                <RangePicker
                    value={rangeValue}
                    onChange={handleRangeChange}
                    allowClear
                />
            </div>
            {loading ? (
                <LazyLoader type="row" />
            ) : (
                <>
                    {lines.length === 0 ? (
                        <div className={styles.EmptyState}>
                            <span>
                                {hasActiveFilter
                                    ? 'No records found for the selected date range.'
                                    : 'No records found.'}
                            </span>
                            {hasActiveFilter && (
                                <Button
                                    type="link"
                                    onClick={onClearFilters}
                                    className={styles.ClearFiltersButton}
                                >
                                    Clear filters
                                </Button>
                            )}
                        </div>
                    ) : (
                        <AppTable
                            columns={columns}
                            data={lines}
                            rowKey={(record: AuditLogLine) =>
                                `${record.time}-${record.user}-${record.type}`
                            }
                            expandedRowRender={expandedRowRender}
                            rowExpandable={hasData}
                            customIcon={true}
                            pagination={false}
                            size="small"
                        />
                    )}
                    {hasMore ? (
                        <div className={styles.LoadMoreContainer}>
                            <Button onClick={onLoadMore} loading={loadingMore}>
                                Load more
                            </Button>
                        </div>
                    ) : hasActiveFilter && lines.length > 0 ? (
                        <div className={styles.LoadMoreContainer}>
                            <Button type="link" onClick={onClearFilters}>
                                Clear filters
                            </Button>
                        </div>
                    ) : null}
                </>
            )}
        </section>
    );
};

export default DataStoreAuditLog;
